import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { HelpCenterDialog } from "./HelpCenterDialog";
import { HelpFab } from "./HelpFab";
import { OnboardingTour } from "./OnboardingTour";
import { useOnboardingTour } from "@/hooks/useOnboardingTour";
import { HELP_EVENTS } from "@/lib/helpEvents";

export function HelpSystem() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState<"kom-igang" | "faq" | "ai">("kom-igang");
  const [tourOpen, setTourOpen] = useState(false);

  const { shouldShow, checked, markComplete, restart } = useOnboardingTour(user);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Auto-start tour för nya inloggade användare på startsidan
  useEffect(() => {
    if (checked && shouldShow && user && location.pathname === "/") {
      // Vänta lite så DOM hinner rendera
      const t = setTimeout(() => setTourOpen(true), 1500);
      return () => clearTimeout(t);
    }
  }, [checked, shouldShow, user, location.pathname]);

  // Lyssna på events
  useEffect(() => {
    const onOpenDialog = (e: Event) => {
      const ce = e as CustomEvent<{ tab?: "kom-igang" | "faq" | "ai" }>;
      setDefaultTab(ce.detail?.tab ?? "kom-igang");
      setDialogOpen(true);
    };
    const onStartTour = () => {
      if (!user) return;
      restart();
      if (location.pathname !== "/") {
        navigate("/");
        // Vänta så DOM hinner rendera startsidan innan rundturen mäter element
        setTimeout(() => setTourOpen(true), 600);
      } else {
        setTourOpen(true);
      }
    };

    window.addEventListener(HELP_EVENTS.OPEN_DIALOG, onOpenDialog);
    window.addEventListener(HELP_EVENTS.START_TOUR, onStartTour);
    return () => {
      window.removeEventListener(HELP_EVENTS.OPEN_DIALOG, onOpenDialog);
      window.removeEventListener(HELP_EVENTS.START_TOUR, onStartTour);
    };
  }, [restart, user]);

  // Dölj FAB på admin-sidor och reset-password
  const hideFab = location.pathname.startsWith("/admin") || location.pathname.startsWith("/reset-password");

  return (
    <>
      {!hideFab && (
        <HelpFab
          onClick={() => {
            setDefaultTab("ai");
            setDialogOpen(true);
          }}
        />
      )}

      <HelpCenterDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultTab={defaultTab}
        isAuthenticated={!!user}
        onStartTour={() => {
          if (!user) return;
          restart();
          if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => setTourOpen(true), 600);
          } else {
            setTourOpen(true);
          }
        }}
      />

      <OnboardingTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onComplete={() => {
          setTourOpen(false);
          markComplete();
        }}
      />
    </>
  );
}
